<!-- src/components/BaiduMapPicker/BaiduMapPicker.vue -->
<template>
  <el-dialog
    :visible.sync="visible"
    title="选择位置"
    width="800px"
    :before-close="handleClose"
    append-to-body
    @open="onOpen"
  >
    <div class="map-wrap">
      <!-- 搜索框 -->
      <div class="search-bar">
        <el-input
          v-model="keyword"
          placeholder="搜索地点"
          clearable
          @keyup.enter.native="searchPlace"
        >
          <el-button slot="append" icon="el-icon-search" @click="searchPlace" />
        </el-input>
      </div>

      <!-- 搜索结果列表 -->
      <div v-if="searchResults.length" class="search-results">
        <div
          v-for="(item, index) in searchResults"
          :key="index"
          class="result-item"
          @click="selectFromSearch(item)"
        >
          <i class="el-icon-location"></i>
          <div class="result-info">
            <div class="result-name">{{ item.name }}</div>
            <div class="result-address">{{ item.address }}</div>
          </div>
        </div>
      </div>

      <!-- 地图容器 -->
      <div ref="mapContainer" class="map-container"></div>

      <!-- 已选位置信息 -->
      <div v-if="selected.address" class="selected-info">
        <i class="el-icon-location-outline"></i>
        <span>{{ selected.address }}</span>
      </div>
    </div>

    <template slot="footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="primary"
        :disabled="!selected.lat"
        @click="confirmSelect"
      >
        确认选择
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
const BAIDU_AK = "JTHkbx0S6LPyftMVJafqLMUTPj3bsAI6"; // 百度地图 AK（请替换为实际的 AK）

export default {
  name: "BaiduMapPicker",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    initialLat: {
      type: Number,
      default: null,
    },
    initialLng: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      visible: false,
      keyword: "",
      searchResults: [],
      selected: {
        name: "",
        address: "",
        lat: null,
        lng: null,
      },
      map: null,
      marker: null,
      geocoder: null,
      localSearch: null,
      poiList: [],
    };
  },
  watch: {
    show(val) {
      this.visible = val;
    },
    visible(val) {
      if (!val) this.$emit("close");
    },
  },
  methods: {
    // 弹窗打开后初始化地图
    onOpen() {
      // 每次打开重置选中状态
      this.selected = { name: "", address: "", lat: null, lng: null };
      this.searchResults = [];
      this.keyword = "";
      this.$nextTick(() => {
        // 若地图已存在（SDK 已加载），直接回显；否则走完整初始化
        if (this.map) {
          const hasInitial = this.initialLat != null && this.initialLng != null;
          if (hasInitial) {
            const BMap = window.BMap;
            const point = new BMap.Point(this.initialLng, this.initialLat);
            this.map.centerAndZoom(point, 15);
            this.setMarker(point);
            this.getAddressByPoint(point);
          } else {
            const BMap = window.BMap;
            const point = new BMap.Point(116.404, 39.915);
            this.map.centerAndZoom(point, 13);
            if (this.marker) {
              this.map.removeOverlay(this.marker);
              this.marker = null;
            }
          }
        } else {
          this.initMap();
        }
      });
    },

    initMap() {
      // 动态加载百度地图 JS SDK
      if (window.BMap && window.BMap.Map) {
        this.createMap();
        return;
      }
      const script = document.createElement("script");
      // 添加随机参数避免缓存问题
      const timestamp = new Date().getTime();
      script.src = `https://api.map.baidu.com/api?v=3.0&ak=${BAIDU_AK}&callback=baiduMapInit&t=${timestamp}`;
      window.baiduMapInit = () => {
        // 确保 BMap.Map 构造函数存在
        if (window.BMap && window.BMap.Map) {
          this.createMap();
        } else {
          console.error("百度地图 SDK 加载失败，BMap.Map 构造函数不存在");
          this.$message.error("地图加载失败，请检查网络连接或 AK 是否正确！");
          this.visible = false;
        }
      };
      script.onerror = () => {
        console.error("百度地图 SDK 加载失败");
        this.$message.error("地图加载失败，请检查网络连接或 AK 是否正确！");
        this.visible = false;
      };
      document.head.appendChild(script);
    },

    createMap() {
      try {
        const BMap = window.BMap;

        if (!BMap || !BMap.Map) {
          console.error("百度地图 SDK 未正确加载，BMap.Map 构造函数不存在");
          this.$message.error("地图加载失败，请刷新页面重试！");
          this.visible = false;
          return;
        }

        if (!this.$refs.mapContainer) {
          console.error("地图容器不存在");
          this.$message.error("地图容器初始化失败，请刷新页面重试！");
          this.visible = false;
          return;
        }

        this.map = new BMap.Map(this.$refs.mapContainer);

        // 若传入了经纬度则回显，否则默认北京
        const hasInitial = this.initialLat != null && this.initialLng != null;
        const centerPoint = hasInitial
          ? new BMap.Point(this.initialLng, this.initialLat)
          : new BMap.Point(116.404, 39.915);

        this.map.centerAndZoom(centerPoint, 15);
        this.map.enableScrollWheelZoom(true);

        // 初始化地理编码和搜索服务
        if (BMap.Geocoder) {
          this.geocoder = new BMap.Geocoder();
        } else {
          console.error("BMap.Geocoder 构造函数不存在");
        }

        if (BMap.LocalSearch) {
          this.localSearch = new BMap.LocalSearch(this.map, {
            renderOptions: { map: null },
            onSearchComplete: this.handleSearchComplete,
          });
        } else {
          console.error("BMap.LocalSearch 构造函数不存在");
        }

        // 若有初始经纬度，打标记并逆地理编码
        if (hasInitial) {
          this.setMarker(centerPoint);
          this.getAddressByPoint(centerPoint);
        }

        // 点击地图选点
        this.map.addEventListener("click", (e) => {
          this.setMarker(e.point);
          this.getAddressByPoint(e.point);
        });
      } catch (error) {
        console.error("创建地图失败:", error);
        this.$message.error("地图初始化失败，请刷新页面重试！");
        this.visible = false;
      }
    },

    // 设置标记点
    setMarker(point) {
      try {
        const BMap = window.BMap;
        if (!BMap || !BMap.Marker || !this.map) {
          console.error("地图未初始化，无法设置标记");
          return;
        }
        if (this.marker) {
          this.map.removeOverlay(this.marker);
        }
        this.marker = new BMap.Marker(point);
        this.map.addOverlay(this.marker);
        this.map.panTo(point);
      } catch (error) {
        console.error("设置标记失败:", error);
      }
    },

    // 逆地理编码：坐标 → 地址
    getAddressByPoint(point) {
      try {
        if (!this.geocoder) {
          console.error("地理编码器未初始化");
          return;
        }
        this.geocoder.getLocation(point, (result) => {
          if (result) {
            this.selected = {
              name:
                result.surroundingPois && result.surroundingPois[0]
                  ? result.surroundingPois[0].title || result.address
                  : result.address,
              address: result.address,
              lat: point.lat,
              lng: point.lng,
            };
          }
        });
      } catch (error) {
        console.error("获取地址失败:", error);
      }
    },

    // 关键字搜索
    searchPlace() {
      if (!this.keyword.trim()) return;
      try {
        if (!this.localSearch) {
          console.error("本地搜索服务未初始化");
          this.$message.error("搜索服务未初始化，请刷新页面重试！");
          return;
        }
        this.localSearch.search(this.keyword);
      } catch (error) {
        console.error("搜索失败:", error);
        this.$message.error("搜索失败，请重试！");
      }
    },

    // 搜索结果回调
    handleSearchComplete(results) {
      this.searchResults = [];
      if (!results) return;
      const count = results.getCurrentNumPois();
      for (let i = 0; i < count; i++) {
        const poi = results.getPoi(i);
        this.searchResults.push({
          name: poi.title,
          address: poi.address,
          point: poi.point,
        });
      }
    },

    // 从搜索结果选择
    selectFromSearch(item) {
      this.searchResults = [];
      this.keyword = item.name;
      this.setMarker(item.point);
      this.selected = {
        name: item.name,
        address: item.address || item.name,
        lat: item.point.lat,
        lng: item.point.lng,
      };
    },

    confirmSelect() {
      this.$emit("select", { ...this.selected });
      this.visible = false;
    },

    handleClose() {
      this.visible = false;
    },
  },
};
</script>

<style scoped>
.map-wrap {
  position: relative;
}

.search-bar {
  margin-bottom: 10px;
}

.search-results {
  position: absolute;
  top: 46px;
  left: 0;
  right: 0;
  z-index: 999;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.result-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  cursor: pointer;
  gap: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.result-item:hover {
  background: #f5f7fa;
}

.result-item:last-child {
  border-bottom: none;
}

.result-info {
  flex: 1;
}

.result-name {
  font-size: 14px;
  color: #303133;
}

.result-address {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}

.map-container {
  width: 100%;
  height: 420px;
  border-radius: 4px;
  overflow: hidden;
}

.selected-info {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f9eb;
  border-radius: 4px;
  font-size: 13px;
  color: #67c23a;
  display: flex;
  align-items: center;
  gap: 6px;
}
</style>



// WEBPACK FOOTER //
// src/components/BaiduMapPicker/BaiduMapPicker.vue